import { useSyncExternalStore } from "react";
import { cn } from "../../lib/utils";

export interface ToastAction {
  label: string;
  onClick: () => void;
  /**
   * Optional stable `data-testid` for the action button (e.g. the AI
   * execution-confirm buttons). Only meaningful for `actions` entries.
   */
  testId?: string;
  /**
   * Keep the toast open after this action is clicked (default: clicking an
   * action dismisses the toast). For actions on a warning that should only
   * leave via explicit dismissal — e.g. the skipped-rows export toast's
   * "Show row"/"Show in folder", where using an action must not eat the
   * missing-data notice. Only meaningful for `actions` entries.
   */
  keepOpen?: boolean;
}

interface Toast {
  id: string;
  title: string;
  description?: string;
  type?: "default" | "success" | "error" | "warning";
  duration?: number;
  action?: ToastAction;
  /**
   * Several actions, rendered as a button row. Independent of `action` — the
   * single-action field and its existing call sites are untouched.
   */
  actions?: ToastAction[];
  /**
   * Skip the auto-dismiss timer entirely. Used by prompts that must wait for
   * an answer. NOTE: `duration` keeps its existing meaning — `duration: 0` is
   * falsy and still yields the 5000 ms default, which other code relies on.
   */
  persist?: boolean;
  /**
   * Fired when the toast is removed WITHOUT one of its actions being clicked
   * (close button, auto-dismiss, or a programmatic `toast.dismiss`). Lets a
   * prompt treat "went away unanswered" as an explicit outcome instead of
   * leaving the caller waiting forever.
   */
  onDismiss?: () => void;
  /**
   * Optional override for the toast's `data-testid` so callers can give
   * specific toasts (e.g. the FK Breadcrumb first-run hint) a stable
   * selector for E2E. When unset, the toast uses the generic `toast` testid.
   */
  testId?: string;
}

interface ToasterProps {
  className?: string;
}

// Create a simple toast context for the application
let toasts: Toast[] = [];
const listeners = new Set<() => void>();

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => toasts;

const createToast = (toast: Omit<Toast, "id">) => {
  const id = Math.random().toString(36).substring(2, 9);
  const newToast = { ...toast, id };
  toasts = [newToast, ...toasts];
  emitChange();

  if (!toast.persist) {
    setTimeout(() => {
      removeToast(id);
    }, toast.duration || 5000);
  }

  return id;
};

/**
 * `viaAction` is set only by an action button, so `onDismiss` means exactly
 * "removed without a choice being made".
 */
const removeToast = (id: string, viaAction = false) => {
  const removed = toasts.find((toast) => toast.id === id);
  const next = toasts.filter((toast) => toast.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emitChange();
  if (!viaAction) removed?.onDismiss?.();
};

const updateToast = (id: string, updates: Partial<Omit<Toast, "id">>) => {
  toasts = toasts.map((t) => (t.id === id ? { ...t, ...updates } : t));
  emitChange();
};

export function Toaster({ className }: ToasterProps) {
  const currentToasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    <div
      data-testid="toast-container"
      className={cn(
        "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px] pointer-events-none",
        className
      )}
      aria-live="polite"
      aria-relevant="additions"
    >
      {currentToasts.map((toast) => {
        // Errors and toasts that will NOT dismiss themselves are announced
        // assertively. A persistent toast is waiting for an answer — the agent
        // execute-with-confirm prompt is the load-bearing case — so queueing it
        // politely behind other output is the wrong behaviour for a control the
        // user is expected to act on. The explicit `aria-live` overrides the
        // container's `polite` for this subtree.
        const isAssertive = toast.type === "error" || toast.persist === true;
        return (
        <div
          key={toast.id}
          data-testid={toast.testId ?? "toast"}
          data-toast-type={toast.type ?? "default"}
          role={isAssertive ? "alert" : "status"}
          aria-live={isAssertive ? "assertive" : "polite"}
          aria-atomic="true"
          className={cn(
            "pointer-events-auto relative flex w-full overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-colors",
            // Multi-action toasts stack their button row under the text; every
            // other toast keeps the original single-row layout byte-for-byte.
            toast.actions?.length
              ? "flex-col items-stretch gap-3"
              : "items-center justify-between",
            "[&[data-state=open]]:animate-in [&[data-state=closed]]:animate-out [&[data-swipe=end]]:animate-out [&[data-state=closed]]:fade-out-80 [&[data-state=closed]]:slide-out-to-right-full [&[data-state=open]]:slide-in-from-top-full",
            "bg-background text-foreground border-border",
            {
              "border-green-500 bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200": toast.type === "success",
              "border-red-500 bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200": toast.type === "error",
              "border-yellow-500 bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200": toast.type === "warning",
            }
          )}
        >
          <div className="grid gap-1">
            {toast.title && <div className="font-semibold">{toast.title}</div>}
            {/* tabular-nums: descriptions that update in place (export progress
                row counts, ETAs) keep every digit the same width, so the text
                doesn't jitter as numbers tick over. */}
            {toast.description && (
              <div className="text-sm opacity-90 whitespace-pre-line tabular-nums">{toast.description}</div>
            )}
          </div>
          {toast.actions && toast.actions.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2">
              {toast.actions.map((toastAction) => (
                <button
                  key={toastAction.label}
                  data-testid={toastAction.testId}
                  onClick={() => { toastAction.onClick(); if (!toastAction.keepOpen) removeToast(toast.id, true); }}
                  className="shrink-0 rounded-md border border-current/20 px-2 py-1 text-xs font-medium hover:bg-black/10 dark:hover:bg-white/10 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-current"
                >
                  {toastAction.label}
                </button>
              ))}
            </div>
          )}
          {toast.action && (
            <button
              onClick={() => { toast.action!.onClick(); removeToast(toast.id); }}
              className="ml-2 shrink-0 rounded-md border border-current/20 px-2 py-1 text-xs font-medium hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
              {toast.action.label}
            </button>
          )}
          <button
            data-testid="toast-close"
            aria-label="Dismiss notification"
            onClick={() => removeToast(toast.id)}
            className="absolute right-2 top-2 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:opacity-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        );
      })}
    </div>
  );
}

// Export the toast functions
export const toast = {
  default: (props: Omit<Toast, "id" | "type">) => createToast({ ...props, type: "default" }),
  success: (props: Omit<Toast, "id" | "type">) => createToast({ ...props, type: "success" }),
  error: (props: Omit<Toast, "id" | "type">) => createToast({ ...props, type: "error" }),
  warning: (props: Omit<Toast, "id" | "type">) => createToast({ ...props, type: "warning" }),
  /** Update an existing toast's properties (title, description, type, action) */
  update: updateToast,
  /** Dismiss a toast by ID (fires its `onDismiss`, if any) */
  dismiss: (id: string) => removeToast(id),
};
